import React, { ReactNode } from "react";
import { Theme } from "@material-ui/core";
import Popover from "@material-ui/core/Popover/Popover";
import Card from "@material-ui/core/Card/Card";
import withStyles from "@material-ui/core/styles/withStyles";
import Typography from "@material-ui/core/Typography/Typography";
import {
  ColumnDataType,
  DetailCardField,
  DetailCardTitleProperty,
  TableColumn,
  DetailCardAction
} from "./types";
import formatDate from "../../utils/formatDate";
import { Button } from "../inputs/Button";

const styles = (theme: Theme) => ({
  button: {
    margin: theme.spacing(1)
  },
  buttonWrapper: {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "center",
    marginTop: theme.spacing(2),
    width: "100%"
  },
  card: {
    maxWidth: "450px",
    padding: theme.spacing(2)
  },
  content: {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "space-between"
  },
  header: {
    marginBottom: theme.spacing(2)
  },
  field: {
    display: "flex",
    flexFlow: "column",
    margin: theme.spacing(1) / 2,
    width: "200px"
  },
  label: {
    fontWeight: 500,
    marginRight: theme.spacing(2)
  }
});

type Props = {
  actions?: DetailCardAction[];
  anchor: { left: number; top: number };
  classes: any;
  columns: TableColumn[];
  data: any;
  open: boolean;
  onClose: () => void;
  renderDetailCard?: (data: DetailCardField[]) => ReactNode;
  titleProperty?: DetailCardTitleProperty;
};

class TableDetailCard extends React.Component<Props> {
  getCardData: () => DetailCardField[];
  title: () => string | null;

  constructor(props: Props) {
    super(props);

    this.title = () => {
      const { data, titleProperty } = props;
      if (!titleProperty) {
        return null;
      }
      if (titleProperty.id && titleProperty.parentId) {
        return data[titleProperty.parentId][titleProperty.id];
      }
      return data[titleProperty.id];
    };

    this.getCardData = () => {
      const { columns, data } = props;
      const cardData: DetailCardField[] = [];
      columns.map(column => {
        if (!column.children) {
          return cardData.push({
            id: column.id,
            type: column.type,
            label: column.label,
            value: data[column.id],
            renderField: column.renderCell
          });
        }
        const parentData = data[column.id];
        return column.children.map(nestedColumn =>
          cardData.push({
            id: nestedColumn.id,
            type: nestedColumn.type,
            group: column.id,
            label: nestedColumn.label,
            value: parentData[nestedColumn.id],
            renderField: nestedColumn.renderCell
          })
        );
      });
      return cardData;
    };
  }

  renderCardRow({ id, label, renderField, type, value }: DetailCardField) {
    const { classes, data } = this.props;
    return (
      <div key={id} className={classes.field}>
        <Typography
          className={classes.label}
          color="textSecondary"
          variant="caption"
        >
          {label}
        </Typography>
        <Typography>
          {renderField
            ? renderField(value, data)
            : type === ColumnDataType.Date
            ? formatDate.condensed(value as Date | number)
            : value}
        </Typography>
      </div>
    );
  }

  render() {
    const {
      actions,
      anchor,
      classes,
      data,
      open,
      onClose,
      renderDetailCard
    } = this.props;
    const cardData = this.getCardData();
    const title = this.title();
    return (
      <Popover
        anchorPosition={anchor}
        anchorReference="anchorPosition"
        onClose={onClose}
        open={open}
        transformOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
      >
        <Card className={classes.card}>
          {title && (
            <Typography className={classes.header} variant="h5">
              {title}
            </Typography>
          )}
          <div className={classes.content}>
            {renderDetailCard
              ? renderDetailCard(cardData)
              : cardData.map(field => this.renderCardRow(field))}
          </div>
          <div className={classes.buttonWrapper}>
            {actions &&
              actions.map((action, index) => (
                <Button
                  id="action"
                  key={index}
                  color="primary"
                  disabled={
                    action.shouldDisable ? action.shouldDisable(data) : false
                  }
                  className={classes.button}
                  onClick={() => action.onClick(data)}
                >
                  {action.label}
                </Button>
              ))}
            <Button id="cancel" className={classes.button} onClick={onClose}>
              Cancel
            </Button>
          </div>
        </Card>
      </Popover>
    );
  }
}

export default withStyles(styles)(TableDetailCard);
